import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { bootstrapAuth } from './api/core'
import './i18n'
import { router } from './router'
import { useAuthStore } from './stores/auth.store'
import './styles/globals.css'

const container = document.getElementById('root')
if (!container) {
  throw new Error('Root container missing in index.html')
}

// L5a — impersonation handoff. The superadmin console opens this app with
// `#impersonate=<jwt>` in the URL fragment (fragment not query so the
// token never hits an access log). We stash the token as the current
// access token — the impersonation JWT's own claims scope the request
// server-side, and the ImpersonationBanner shows the tenant user which
// session is active + a live countdown.
try {
  const hash = window.location.hash
  const marker = '#impersonate='
  if (hash.startsWith(marker)) {
    const token = decodeURIComponent(hash.slice(marker.length))
    if (token) {
      sessionStorage.setItem('impersonation_token', token)
      useAuthStore.getState().setTokens({ access_token: token, refresh_token: '' } as never)
      history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }
} catch {
  // Any browser storage failure means we skip impersonation handoff and
  // land on the normal login flow. Nothing to log.
}

// SEC-1: the refresh token lives in an HttpOnly cookie and the access
// token is in-memory only. On page load we have neither in JS, so we
// hydrate auth state before mounting the router. The router's beforeLoad
// guards still work — they'll see isAuthenticated=true with a real access
// token, or a cleared store.
//
// PERF-8: a single /v1/auth/bootstrap call folds the cookie-driven refresh
// and the /me read into one round-trip (was refresh + me serially).
//
// PERF audit Lot 3.3 (2026-07-09): bootstrapAuth() is now timeout-bounded
// (see api/core.ts), but even a bounded multi-second wait on a cold Render
// start left the page blank the whole time — nothing rendered until this
// promise settled. hasRendered/renderApp below let the router mount
// optimistically once BOOT_RACE_MS elapses, while bootAuth keeps running
// in the background: apiFetch's own 401→refresh flow covers any request
// fired before the real token lands, and if bootAuth turns out to have
// failed (dead cookie) after the optimistic mount, we force-navigate back
// to login instead of leaving stale authenticated UI up.
let hasRendered = false

function renderApp(): void {
  if (hasRendered) return
  hasRendered = true
  createRoot(container as HTMLElement).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  )
}

async function bootAuth(): Promise<void> {
  const wasAuthed = useAuthStore.getState().isAuthenticated
  if (!wasAuthed) return
  const data = await bootstrapAuth()
  if (!data) {
    useAuthStore.getState().clearAuth()
    if (hasRendered) void router.navigate({ to: '/auth/login' })
    return
  }
  useAuthStore.getState().setAuth(data.me.user, data.me.org, data.me.permissions ?? [], data.tokens)
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const BOOT_RACE_MS = 3_000

const bootPromise = bootAuth()
void Promise.race([bootPromise, sleep(BOOT_RACE_MS)]).finally(renderApp)
